import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@blueprintjs/core";
import classNames from "classnames";
import { handlePlotHover, preparePlotLayout, prepareRangeConfigsOnLockButtonClick, } from "../GraphElementGraph/utils";
import { copyToClipboard } from "utils/data";
import { getDetailedDurationString, getSecondsFromConvertedTime } from "utils/dateTime";
import { getNumberFromNumberString, getTextContentFromHtml } from "utils/string";
import { getResultElementSourceComputedName, prepareProcessDataViewerGraphData, } from "store/rs-workspace/utils/results.utils";
import { getSimulationPreviewXAxisPointValue } from "store/rs-workspace/utils/scenario.utils";
import ResultInfoMessage, { ResultInfoMessageType } from "components/ResultInfoMessage";
import Sources from "./components/Sources";
import XAxisRow from "./components/XAxisRow";
import GraphElementGraph from "../GraphElementGraph";
import GraphSourcesLabels from "../GraphSourcesLabels";
import IconButton from "components/IconButton";
import { calculationLineGraphElementResultSelector, processDataViewerGraphPointsSelector, processDataViewerResultsTimeUnitSelector, } from "store/rs-workspace/selectors/results.selectors";
import { persistedInsightsDataSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { processVariablesSelector } from "store/global/global.selectors";
import { ResultElementGraphViewType, ResultElementLineType, ResultSetElementType, SimulationRunState, } from "store/rs-workspace/rs-workspace.types";
import { setPlotRangeConfigs, updatePlotSourceConfigs } from "store/rs-workspace/actions/results.actions";
import { SourceFields } from "./types";
import useMessageTimer from "hooks/useMessageTimer";
import AutoscaleIcon from "icons/autoscale.svg";
import GridlinesIcon from "icons/grid.svg";
import ErrorIcon from "icons/info_triangle.svg";
import * as styles from "./styles.module.scss";
export const defaultSource = {
    [SourceFields.view]: true,
    [SourceFields.source]: null,
    [SourceFields.viewType]: ResultElementGraphViewType.MedianWithSpread,
    [SourceFields.lineType]: ResultElementLineType.Unknown,
};
const ProcessDataViewerElement = ({ form, elementData, restrictions, resultSetId }) => {
    const dispatch = useDispatch();
    const updatedRangeConfigsRef = useRef();
    const [layoutRevisionNo, setLayoutRevisionNo] = useState(0);
    const [showCopiedToClipboardMessage, setShowCopiedToClipboardMessage] = useMessageTimer();
    const recipes = useSelector(workspaceRecipesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const processVariables = useSelector(processVariablesSelector);
    const processData = useSelector(workspaceProcessDataSelector);
    const graphResults = useSelector(calculationLineGraphElementResultSelector({ resultSetId, resultSetElementId: elementData.id }, ResultSetElementType.ProcessDataViewer));
    const timeUnit = useSelector(processDataViewerResultsTimeUnitSelector({ resultSetId, resultSetElementId: elementData.id }));
    const points = useSelector(processDataViewerGraphPointsSelector({ resultSetId, resultSetElementId: elementData.id }));
    const editMode = useMemo(() => !!form, [form]);
    const graphData = useMemo(() => prepareProcessDataViewerGraphData(points, elementData.sources), [elementData.sources, points]);
    const someResultBiased = useMemo(() => graphResults?.some(({ source, hasErrors }) => source.view && hasErrors), [graphResults]);
    const fetchingMap = useMemo(() => graphResults?.map(({ sourceId, fetching, state }) => ({
        sourceId,
        inProgress: fetching || state === SimulationRunState.STARTED,
    })), [graphResults]);
    const plotLayout = useMemo(() => preparePlotLayout(graphResults, elementData.xPlotConfig, updatedRangeConfigsRef.current, {
        xaxis: { title: timeUnit ? `Time (${timeUnit})` : "Time" },
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [elementData.xPlotConfig, graphResults, timeUnit, layoutRevisionNo]);
    const onPlotHover = useCallback((plotRef) => (ev) => handlePlotHover(plotRef, (legendTitleInnerHtml) => getDetailedDurationString(getSecondsFromConvertedTime(getNumberFromNumberString(legendTitleInnerHtml), timeUnit)), ev), [timeUnit]);
    const onCopyPointData = useCallback((e) => {
        const text = e.points?.reduce((acc, clickedPoint) => {
            const sourceResult = graphResults?.find(({ sourceId }) => sourceId === clickedPoint.customdata);
            const originalPoint = sourceResult?.points?.find(({ point }) => getSimulationPreviewXAxisPointValue(point?.x, timeUnit) === clickedPoint.x);
            const { point } = { ...originalPoint };
            const { x, median, spread, average, sd } = { ...point };
            const xText = `X - ${getDetailedDurationString(x ?? 0)}`;
            const yText = `Y - ${sourceResult?.sourceLineTitle}`;
            const sourceName = getResultElementSourceComputedName(sourceResult?.source?.source ?? null, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData);
            const unit = sourceResult?.sourceLineUnits
                ? ` ${getTextContentFromHtml(sourceResult?.sourceLineUnits)}`
                : "";
            const mainText = `Median - ${median}${unit}\r\nSpread - ${spread}${unit}\r\nMean - ${average}${unit}\r\nSD - ${sd}${unit}`;
            const pointText = `${xText}\r\n${yText}\r\n${sourceName}\r\n${mainText}`;
            return `${acc}\r\n\r\n${pointText}`;
        }, "");
        copyToClipboard(text);
        setShowCopiedToClipboardMessage(true);
    }, [
        graphResults,
        insights,
        optimizations,
        persistedInsightsData,
        processData,
        processVariables,
        recipes,
        setShowCopiedToClipboardMessage,
        simulations,
        timeUnit,
    ]);
    const handleRelayout = useCallback((ranges, shouldUpdateLayout) => dispatch(updatePlotSourceConfigs({
        payload: {
            elementType: elementData.type,
            elementId: elementData.id,
            resultSetId,
            ranges,
        },
        callback: (updatedRangeConfigs) => {
            updatedRangeConfigsRef.current = updatedRangeConfigs;
            shouldUpdateLayout && setLayoutRevisionNo((prevState) => prevState + 1);
        },
    })), [dispatch, elementData.id, elementData.type, resultSetId]);
    const onLockButtonClick = useCallback((locked, axisName, responseId) => {
        handleRelayout(prepareRangeConfigsOnLockButtonClick(plotLayout, updatedRangeConfigsRef, locked, axisName, responseId), true);
    }, [handleRelayout, plotLayout]);
    const renderHeader = useCallback((isAllAxesAutoscaled, isAllGridlinesVisible, handleAutoscaleButtonClick, handleGridlinesButtonClick) => (React.createElement("div", { className: styles.top },
        someResultBiased && (React.createElement("div", { className: classNames(styles.biasedInfo, { [styles.shifted]: (graphResults?.length ?? 0) > 1 }) },
            React.createElement(Tooltip, { minimal: true, content: "At least one calculated value is biased.", placement: "bottom", popoverClassName: styles.tooltip, rootBoundary: "viewport", openOnTargetFocus: false },
                React.createElement(ErrorIcon, { className: styles["error-icon"] })))),
        React.createElement(IconButton, { classes: { root: styles.autoscaleIcon }, icon: React.createElement(AutoscaleIcon, { className: classNames({
                    [styles["autoscaleIcon-icon-autoscaled"]]: isAllAxesAutoscaled,
                }) }), small: true, title: "Autoscale", onClick: handleAutoscaleButtonClick }),
        React.createElement(IconButton, { classes: { root: styles.gridIcon }, icon: React.createElement(GridlinesIcon, { className: classNames({
                    [styles["gridIcon-active"]]: isAllGridlinesVisible,
                }) }), small: true, title: "Gridlines", onClick: () => handleGridlinesButtonClick && handleGridlinesButtonClick(!isAllGridlinesVisible) }),
        React.createElement(GraphSourcesLabels, { sources: elementData?.sources }))), [elementData?.sources, graphResults?.length, someResultBiased]);
    const renderConfig = useCallback((axesRagesConfig, handleAutoscaleButtonClick) => {
        if (!editMode && !elementData.showSettings) {
            return;
        }
        return (React.createElement("div", { className: styles.config },
            !editMode && (React.createElement(XAxisRow, { rangeConfig: axesRagesConfig?.xaxis, halfWidth: !editMode && !elementData.fullView, handleAutoscaleButtonClick: handleAutoscaleButtonClick, onLockButtonClick: onLockButtonClick })),
            React.createElement(Sources, { editMode: editMode, elementData: elementData, restrictions: restrictions, form: form, fetchingMap: fetchingMap, axesRanges: axesRagesConfig, handleAutoscaleButtonClick: handleAutoscaleButtonClick, onLockButtonClick: onLockButtonClick })));
    }, [editMode, elementData, fetchingMap, form, onLockButtonClick, restrictions]);
    useEffect(() => {
        return () => {
            if (updatedRangeConfigsRef.current) {
                dispatch(setPlotRangeConfigs({
                    resultSetId,
                    elementId: elementData.id,
                    ranges: updatedRangeConfigsRef.current,
                }));
            }
        };
    }, [resultSetId, elementData.id, dispatch]);
    return (React.createElement("div", { className: styles.root },
        React.createElement(ResultInfoMessage, { type: ResultInfoMessageType.COPIED_TO_CLIPBOARD, visible: showCopiedToClipboardMessage, onClose: () => setShowCopiedToClipboardMessage(false), className: styles["copied-to-clipboard-message"] }),
        !editMode && (React.createElement(GraphElementGraph, { plotLayout: plotLayout, graphData: graphData ?? [], onPlotHover: onPlotHover, onRelayout: handleRelayout, renderHeader: renderHeader, renderConfig: renderConfig, onCopy: onCopyPointData })),
        editMode && renderConfig()));
};
export default ProcessDataViewerElement;
